import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule, MatSuffix } from '@angular/material/form-field';
import { MoneyPipe } from '../../../shared/pipes/money.pipe';
import { FormGroupPipe } from '../../../shared/pipes/form-group.pipe';
import { MatInputModule } from '@angular/material/input';
import { InventoryActionProduct, toInventoryActionProductForm } from '../../../models/inventory-action-product';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { AdminService } from '../../../admin/services/admin.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ProductsDialogComponent } from '../../../shared/common-forms/products-dialog/products-dialog.component';
import { Product } from '../../../models/product.model';
import { ProductsService } from '../../../services/products.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-invoice-details-form',
  imports: [
    CurrencyMaskModule,
    FormGroupPipe,
    MatFormFieldModule,
    MatIcon,
    MatIconButton,
    MatInputModule,
    MatSuffix,
    MatTooltipModule,
    MoneyPipe,
    ReactiveFormsModule,
  ],
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss'
})
export class InvoiceDetailsFormComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns = {
    move_actions: '',
    name: 'Producto',
    quantity: 'Cantidad',
    amount: 'Precio unitario',
    discount_percent: 'Descuento',
    total: 'Subtotal Neto',
    actions: '',
  };

  formArray = input.required<FormArray>();

  onPaste: () => void;

  original = input.required<InventoryActionProduct[]>();


  private readonly adminService = inject(AdminService);

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly fb = inject(FormBuilder);

  private readonly matDialog = inject(MatDialog);

  private readonly productService = inject(ProductsService);

  private readonly snackBar = inject(MatSnackBar);


  get columnsNames() {
    return Object.keys(this.displayedColumns);
  }

  get Tax() {
    return this.adminService.getTaxValue();
  }

  get Total() {
    return this.formArray().controls.reduce((acc, _, index) => {
      const value = this.getItemTotal(index);
      return acc + value;
    }, 0); 
  }

  ngOnInit(): void {
    this.setFormControlHandlers();
  }

  ngAfterViewInit(): void {
    this.onPaste = () => {

      navigator.clipboard.readText().then((text) => {
        const skus = text.split('\n').filter((sku) => sku.length > 0);
        const plural = skus.length > 1;
        this.cdr.markForCheck();
        this.snackBar.open(`Pegando ${skus.length} producto${plural ? 's':''}`, 'Cerrar', {
          duration: 3000,
          verticalPosition: 'bottom',
          horizontalPosition: 'center',
        });
        Promise.allSettled(skus.map((sku) => this.pasteProduct(sku.trim()))).then((results) => {
          this.cdr.markForCheck();

          const failed = results.filter((result) => result.status === 'rejected');
          const succeded = results.filter((result) => result.status === 'fulfilled');
          const succededPlural = succeded.length > 1;
          let duration = 3000;
          let message = '';
          if (failed.length) {
            duration = 6000;
            message += failed.map((result) => result.reason).join('. ') + '. ';
          }

          if (succeded.length) {
            message += `${succeded.length} producto${succededPlural ? 's':''} pegado${succededPlural ? 's':''}.`;
          }
          this.snackBar.open(message, 'Cerrar', {
            duration,
            verticalPosition: 'bottom',
            horizontalPosition: 'center',
            panelClass: ['snackbar-success']
          });
        })
      });
    }

    document.addEventListener('paste', this.onPaste);
  }

  selectProduct() {
    this.matDialog.open(ProductsDialogComponent, {
      data: { 
        showBuyPrice: true,
      },
      minWidth: '80%',
    }).afterClosed().subscribe((product: Partial<Product>) => {
      if (!product) return;

      if (typeof product === 'object') {
        this.addProductControl(product);
      }  
      
    });
  }

  private addProductControl(product: Partial<Product>) {
    const control = this.generateFormGroup(product);
    this.formArray().push(control);
    this.formArray().markAsDirty();
    this.formArray().markAsTouched();
    this.cdr.markForCheck();
  }

  private generateFormGroup({
      id = null,
      category_ids = null,
      name = '',
      code = '',
      price = 0,
      quantity = 1,
      unit = 'Unidad',
    } = {}) : FormGroup {
      const controlGroup = this.fb.group({
        product_id: id,
        product: this.fb.group({
          name,
          code,
        }),
        quantity: [{ value: quantity, disabled: !quantity }],
        discount_percent: 0,
        unit,
        amount: price,
        category_id: category_ids ? category_ids[0] : null,
      });
  
      controlGroup.get('product_id').setValidators(Validators.required);
      controlGroup.get('category_id').setValidators(Validators.required);
      controlGroup.get('product.code').setValidators(Validators.required);
      controlGroup.get('quantity').setValidators([Validators.required, Validators.min(1)]);
      controlGroup.get('amount').setValidators([Validators.required, Validators.min(0)]);
  
      return controlGroup;
    }

  
  pasteProduct(sku: string) {
    return new Promise<void>((resolve, reject) => {
      this.productService.select(sku).subscribe((products) => {
        const product = products.items.at(0);
        if (!product) {
          reject(`No se encontró el producto con SKU: ${sku}`);
          return;
        }
  
        this.addProductControl(product);
        resolve();
      });
    });

  }

  private setFormControlHandlers() {

  }

  resetProducts(): void {
    this.formArray().clear();
    this.original().forEach(product => {
      this.formArray().push(
        toInventoryActionProductForm(product)
      );
    });
  }

  removeItem(index: number): void {
    this.formArray().removeAt(index);
    this.formArray().markAsDirty();
    this.formArray().markAsTouched();
  }

  moveItemUp(index: number): void {
    if (index > 0) {
      const control = this.formArray().at(index);
      this.formArray().removeAt(index);
      this.formArray().insert(index - 1, control);
      this.formArray().markAsDirty();
      this.formArray().markAsTouched();
    }
  }

  moveItemDown(index: number): void {
    if (index < this.formArray().length - 1) {
      const control = this.formArray().at(index);
      this.formArray().removeAt(index);
      this.formArray().insert(index + 1, control);
      this.formArray().markAsDirty();
      this.formArray().markAsTouched();
    }
  }

  getItemTotal(index: number): number {
    const item = this.formArray().at(index)?.value;

    if (!item || !item.amount || !item.quantity) {
      return 0;
    }

    return item.amount * item.quantity * (1 - (item.discount_percent || 0) / 100);
  }

  getTotal(): number {
    return this.formArray().controls.reduce((acc, control) => {
      const value = control.value;
      return acc + (value.amount || 0) * (value.quantity || 0) * (1 - (value.discount_percent || 0) / 100);
    }, 0);
  }

  ngOnDestroy(): void {
    document.removeEventListener('paste', this.onPaste);
    this.cdr.detach();
  }
}
